import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Loader2, RefreshCw, BarChart3, Activity, Package, 
  ShoppingCart, Factory, Target, DollarSign
} from 'lucide-react';
import { dashboardApiService, PlantHeadDashboardData } from '../services/dashboardApi';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

export function PlantHeadDashboard() {
  const { selectedBranch } = useBranch();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<PlantHeadDashboardData | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    loadDashboardData();
  }, [selectedBranch, startDate, endDate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardApiService.getPlantHeadDashboard(
        selectedBranch || undefined,
        startDate,
        endDate
      );
      if (response.success && response.data) {
        setDashboardData(response.data);
      } else {
        toast.error(response.message || 'Failed to load dashboard data');
      }
    } catch (error) {
      // console.error('Error loading plant head dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">No data available</p>
          <Button onClick={loadDashboardData} variant="outline" className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const COLORS = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <Factory className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Plant Head Dashboard</CardTitle>
                  <p className="text-gray-600 text-sm mt-1">
                    {dashboardData.lastUpdated && `Last updated: ${new Date(dashboardData.lastUpdated).toLocaleString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label htmlFor="startDate" className="text-white text-sm">From:</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-40 bg-white/20 border-white/30 text-white placeholder:text-white/70"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="endDate" className="text-white text-sm">To:</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-40 bg-white/20 border-white/30 text-white placeholder:text-white/70"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDashboardData}
                  disabled={loading}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Operational KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">PO Fulfillment</p>
                  <p className="text-2xl font-bold text-primary-600 mt-1">
                    {formatPercentage(dashboardData.operationalKPIs.purchaseOrderFulfillmentRate)}
                  </p>
                </div>
                <Target className="h-8 w-8 text-primary-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">On-Time Delivery</p>
                  <p className="text-2xl font-bold text-blue-600 mt-1">
                    {formatPercentage(dashboardData.operationalKPIs.onTimeDeliveryRate)}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Inventory Turnover</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {dashboardData.operationalKPIs.inventoryTurnover.toFixed(2)}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Production Efficiency</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">
                    {formatPercentage(dashboardData.operationalKPIs.productionEfficiency)}
                  </p>
                </div>
                <Factory className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Material Yield</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    {formatPercentage(dashboardData.operationalKPIs.materialYield)}
                  </p>
                </div>
                <Package className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Summary */}
        <Card className="border-primary-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Alerts & Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-sm text-gray-600">Overdue POs</p>
                  <p className="text-xl font-bold">{dashboardData.alerts.overduePOs}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm text-gray-600">Overdue Work Orders</p>
                  <p className="text-xl font-bold">{dashboardData.alerts.overdueWorkOrders}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-sm text-gray-600">Low Stock Alerts</p>
                  <p className="text-xl font-bold">{dashboardData.alerts.lowStockAlerts}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <AlertTriangle className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600">Quality Issues</p>
                  <p className="text-xl font-bold">{dashboardData.alerts.qualityIssues}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Purchase Trends Chart */}
          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Purchase Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dashboardData.purchaseTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="poValue" stroke="#ef4444" name="PO Value" />
                  <Line type="monotone" dataKey="grnValue" stroke="#3b82f6" name="GRN Value" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Sales Trends Chart */}
          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Sales Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dashboardData.salesTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="monthName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="orderValue" fill="#10b981" name="Order Value" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Supplier Performance Overview */}
        <Card className="border-primary-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Top 10 Supplier Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.supplierPerformanceOverview.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No supplier performance data</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">Performance Score</TableHead>
                    <TableHead className="text-right">On-Time Delivery %</TableHead>
                    <TableHead className="text-right">Rejection Rate %</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.supplierPerformanceOverview.slice(0, 10).map((supplier) => (
                    <TableRow key={supplier.supplierId}>
                      <TableCell className="font-medium">{supplier.supplierName}</TableCell>
                      <TableCell className="text-right">
                        <span className={supplier.performanceScore >= 70 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {supplier.performanceScore.toFixed(1)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{formatPercentage(supplier.onTimeDeliveryPercentage)}</TableCell>
                      <TableCell className="text-right">{formatPercentage(supplier.rejectionRate)}</TableCell>
                      <TableCell>
                        {supplier.performanceScore >= 70 ? (
                          <Badge variant="outline" className="text-green-600">Good</Badge>
                        ) : (
                          <Badge variant="destructive">Low</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Inventory Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Inventory Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Total Inventory Value</span>
                  <span className="text-lg font-bold">{formatCurrency(dashboardData.inventoryHealth.totalInventoryValue)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Inventory Turnover Ratio</span>
                  <span className="text-lg font-bold">{dashboardData.inventoryHealth.inventoryTurnoverRatio.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Slow-Moving Items</span>
                  <span className="text-lg font-bold">{dashboardData.inventoryHealth.slowMovingItemsCount} ({formatCurrency(dashboardData.inventoryHealth.slowMovingItemsValue)})</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Fast-Moving Items</span>
                  <span className="text-lg font-bold">{dashboardData.inventoryHealth.fastMovingItemsCount}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary-200 bg-white">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">WIP Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Total WIP Value</span>
                  <span className="text-lg font-bold">{formatCurrency(dashboardData.wipAnalysis.totalWIPValue)}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Average WIP Days</span>
                  <span className="text-lg font-bold">{dashboardData.wipAnalysis.averageWIPDays.toFixed(1)} days</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Stuck Work Orders</span>
                  <span className="text-lg font-bold text-red-600">{dashboardData.wipAnalysis.stuckWorkOrders}</span>
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm text-gray-600">Work Orders in Progress</span>
                  <span className="text-lg font-bold">{dashboardData.wipAnalysis.wipByStatus.reduce((sum, s) => sum + s.count, 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Production Efficiency */}
        <Card className="border-primary-200 bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Production Efficiency</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-2">Completion Rate</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatPercentage(dashboardData.productionEfficiency.workOrderCompletionRate)}
                </p>
              </div>
              <div className="p-4 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-2">Average Cycle Time</p>
                <p className="text-3xl font-bold text-blue-600">
                  {dashboardData.productionEfficiency.averageProductionCycleTime.toFixed(1)} days
                </p>
              </div>
              <div className="p-4 border rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-2">On-Time Delivery Rate</p>
                <p className="text-3xl font-bold text-purple-600">
                  {formatPercentage(dashboardData.productionEfficiency.onTimeDeliveryRate)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

